import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { GlobalConfigAPI } from '@martinus/global-config/api/GlobalConfigAPI.js';
import { SGConfig } from '@martinus/global-config/db/sg-config.js';
import { html } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';

const infoText = html` Templates sind Datein, welche mit Daten aus der Datenbank befüllt werden. Eine Template-Datei
beinhaltet eine spezielle Syntax um Felder zu beschreiben. Im Anschluss kann das System diese Felder mit Daten füttern
und ein fertiges Dokument drucken.`;

const loadingSkeleton = html`
  <div class="card p-4 m-4">
    <p>${infoText}</p>
    <div class="card-body">
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
      <div class="skeleton h-8 w-full"></div>
    </div>
  </div>
`;

function renderTemplateField(sgConfig: SGConfig, field: string, label: string) {
  const template = sgConfig[field] as AdornisFile | null;

  const handleUpload = async () => {
    const fileList = await askForFile({ acceptFileTypes: '.docx', acceptMultipleFiles: false });
    if (!fileList) throw new Error('Es gab einen Fehler beim hochladen der Datei');
    const file = fileList[0];
    if (!file) throw new Error('Es gab einen Fehler beim hochladen der Datei');

    const fileID = await AdornisFile.upload(file);

    sgConfig[field] = file;
    sgConfig[`${field}ID`] = fileID;

    try {
      await sgConfig.save();

      SgSnackbar.show(`${label} wurde gespeichert.`);
    } catch (error) {
      console.error(error);
      SgSnackbar.show('Fehler beim Speichern der Konfiguration');
    }
  };

  const handleDownload = () => {
    if (template) goTo(AdornisFile.getDownloadLink(template._id), { target: '_blank' });
  };

  return html`
    <div
      class="flex flex-row items-center p-4 hover:bg-base-300 cursor-pointer rounded justify-between"
      @click=${() => (template ? handleDownload() : handleUpload())}
    >
      <div class="grid grid-cols-[400px_1fr]">
        <span class="text-sm font-semibold text-base-content/70 flex-1"> ${label} </span>
        <span class="flex-1">
          ${
            template
              ? (template.meta?.completeName ?? 'Datei wird durch erneutes Laden aktualisiert')
              : 'Keine Datei ausgewählt'
          }
        </span>
      </div>
      ${
        template
          ? html`
              <button
                type="button"
                class="btn btn-square btn-ghost btn-sm tooltip hover:text-error"
                aria-label="Löschen"
                data-tip="Löschen"
                ${hoverTooltip()}
                @click=${async (e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();

                  const confirm = await confirmDialog(`${label} wirklich löschen?`);
                  if (!confirm) return;

                  await template.remove();

                  sgConfig[field] = null;
                  sgConfig[`${field}ID`] = null;

                  await guardDialog(
                    async () => {
                      await sgConfig.save();
                    },
                    {
                      success: () => SgSnackbar.show(`${label} wurde erfolgreicht gelöscht`),
                    },
                  );
                }}
              >
                <span class="icon-[material-symbols--delete-rounded] size-5"></span>
              </button>
            `
          : html` <div></div> `
      }
    </div>
  `;
}

export const SGAdminDashboardTemplateFiles = stateful(() => {
  const { value: config, loading: configLoading } = useQuery(
    () => GlobalConfigAPI.subscribe()(selectionSet(() => SGConfig, 3)),
    [],
  );

  if (configLoading || !config) {
    return loadingSkeleton;
  }

  return html`
    <div class="card-container">
      <div class="card flex flex-col p-4 gap-2">
        <h2 class="font-semibold md:text-lg">Vertragstemplates</h2>

        ${renderTemplateField(config, 'contractTemplate', 'Vertrags Template')}
        ${renderTemplateField(config, 'gestellungsTemplate', 'Gestellungs Template')}
        ${renderTemplateField(config, 'supplementTemplate', 'Nachtrags Template')}
        ${renderTemplateField(config, 'gestellungsSupplementTemplate', 'Gestellungs Nachtrag Template')}
        ${renderTemplateField(config, 'psbAVInformationDocument', 'PSB-AV Dokument Template')}
      </div>

      <div class="card flex flex-col p-4 gap-2">
        <h2 class="font-semibold md:text-lg">Quittungstemplates</h2>
        ${renderTemplateField(config, 'quittungTemplate', 'Quittung Template')}
        ${renderTemplateField(config, 'spenderQuittung', 'Spenderquittung Template')}
      </div>

      <div class="card flex flex-col p-4 gap-2">
        <h2 class="font-semibold md:text-lg">Eintrittsformulartemplates</h2>
        ${renderTemplateField(config, 'erklaerungStrafverfahrenTemplate', 'Erklärung Strafverfahren Template')}
        ${renderTemplateField(config, 'antragFuehrungszeugnisTemplate', 'Antrag Führungszeugnis Template')}
        ${renderTemplateField(config, 'antragGesundheitszeugnisTemplate', 'Antrag ärztliche Bescheinigung Template')}
        ${renderTemplateField(config, 'kenntnisnameInfektionsschutzTemplate', 'Infektionsschutz Template')}
        ${renderTemplateField(config, 'personalbogenTemplate', 'Personalbogen Template')}
        ${renderTemplateField(config, 'masernschutzTemplate', 'Masernschutz Template')}
      </div>

      <div class="card flex flex-col p-4 gap-2">
        <h2 class="font-semibold md:text-lg">BG-ADD Templates</h2>
        ${renderTemplateField(config, 'beschaeftigungsgenehmigungTemplate', 'Grundantrag Template')}
      </div>
    </div>
  `;
});
